import React from 'react';
import { theme, tintOf } from '../theme';
import { CheckIcon, InsteadIcon } from './shared';
import { SMALL_FACT_METRICS } from './SplitPill';

// <purpose>THE decision badge (small facts on a row, round 4 A.4, operator 2026-10-19): the clerk's
// primary-axis word, as the primary axis's own squared filled badge, split into an ICON KEY (the
// act: a check for approved-as, the instead-arrow for declined-instead) on a 22 % tint of the hue
// with the hue as ink, and the served WORD white on the hue. One component for the two acts that
// used to wear two faces (WIM's outlined resolution pill and the info pill „Stattdessen: <word>").
// Last element right on row 3, on the detail's decision line and on the Archiv row.
// Registry: design/design-languages.html, entry `primary_axis.decision`.</purpose>
// <dependencies>tintOf (theme) derives the key ground from the hue — never a second token. The hue
// itself is the served hue of the chosen word (cm.color). A decline whose word is the chooser's own
// (Von Hand beantwortet, Kein Vorgang) or absent has NO hue: it wears the OUTLINED face (white
// ground, navy ink and border). On a gMSB tenant every axis hue is a K tier, so a borrowed fill
// (navy-light #555E99 sat beside K3A #4A517F) read as a tier the clerk never chose; no axis value
// wears the outline. Operator ruling 2026-10-27 (B5 walk).</dependencies>
// <constraints>A decline without a word renders the key segment ALONE (no empty value segment, no
// divider). No hover: the word is the long form (house rule: hover only when it reduces load).
// Fixed height per variant so the badge landing changes no row height.</constraints>
export type DecisionAct = 'approved' | 'instead';
export type DecisionBadgeVariant = 'row' | 'detail';

/** The share of the hue in the key segment's ground (the rest is white). */
export const DECISION_KEY_TINT = 0.22;

// Size, icon and height are the small-fact metrics the split pill shares (SMALL_FACT_METRICS): one
// table, so the badge and the traces beside it on row 3 can never end up at two heights.
const METRICS: Record<DecisionBadgeVariant, { fontSize: number; keyPadding: string; valuePadding: string; iconSize: number; height: number }> = {
  row:    { ...SMALL_FACT_METRICS.row, keyPadding: '0 5px', valuePadding: '0 6px' },
  detail: { ...SMALL_FACT_METRICS.detail, keyPadding: '0 6px', valuePadding: '0 8px' },
};

export const DecisionBadge: React.FC<{
  act: DecisionAct;
  /** The served hue the badge wears — the chosen word's own colour; null = the outlined face (a
   *  word that is not an axis value, so it has no colour of its own). */
  hue: string | null;
  /** The served word, or null for a decline filed without one (the key segment alone). */
  word: string | null;
  /** The accessible name of the KEY-ONLY form (no word to read): the verdict's own word, e.g.
   *  „Nicht hilfreich". Ignored when a word is shown — the word is the name. */
  label?: string;
  variant?: DecisionBadgeVariant;
  testId?: string;
  style?: React.CSSProperties;
}> = ({ act, hue, word, label, variant = 'row', testId, style }) => {
  const m = METRICS[variant];
  const outlined = hue === null;
  const ink = outlined ? theme.colors.navy : hue;
  return (
    <span
      data-testid={testId}
      data-act={act}
      role={word ? undefined : 'img'}
      aria-label={word ? undefined : label}
      style={{
        display: 'inline-flex', alignItems: 'stretch', boxSizing: 'border-box',
        height: m.height, borderRadius: 4, overflow: 'hidden',
        // The outline is drawn inside the box (boxSizing above), so both faces keep one height.
        border: outlined ? `1px solid ${ink}` : undefined, backgroundColor: outlined ? theme.colors.white : undefined,
        fontSize: m.fontSize, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0,
        ...style,
      }}
    >
      <span
        style={{ display: 'flex', alignItems: 'center', padding: m.keyPadding, backgroundColor: outlined ? undefined : tintOf(ink, DECISION_KEY_TINT), color: ink }}
      >
        {act === 'approved' ? <CheckIcon stamp size={m.iconSize} color={ink} /> : <InsteadIcon size={m.iconSize} color={ink} />}
      </span>
      {word !== null && word !== '' && (
        <span style={outlined
          ? { display: 'inline-flex', alignItems: 'center', padding: m.valuePadding, borderLeft: `1px solid ${ink}`, color: ink }
          : { display: 'inline-flex', alignItems: 'center', padding: m.valuePadding, backgroundColor: ink, color: theme.colors.white }}>
          {word}
        </span>
      )}
    </span>
  );
};
